import React from 'react';
import { InterviewPrepData } from '@/api/types';
import { Badge } from '@/components/ui/Badge';

interface InterviewContentProps {
  data: InterviewPrepData;
  activeTab: string;
}

export const InterviewContent: React.FC<InterviewContentProps> = ({ data, activeTab }) => {
  if (activeTab === 'behavioral') {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Behavioral Questions</h3>
          <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{data.behavioral.description}</p>
        </div>
        <div className="space-y-4">
          {data.behavioral.questions.map((q, i) => (
            <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm">
              <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3">{q.question}</h4>
              <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-800/50 rounded-lg p-4 text-sm text-slate-800 dark:text-slate-200">
                <span className="font-semibold text-emerald-800 dark:text-emerald-300 block mb-1">Advice/Strategy:</span>
                {q.advice}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (activeTab === 'technical') {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Technical Questions</h3>
          <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{data.technical.description}</p>
        </div>
        <div className="space-y-4">
          {data.technical.questions.map((q, i) => (
            <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm">
              <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3">{q.question}</h4>
              <div className="bg-blue-50/50 dark:bg-blue-900/10 rounded-lg p-4 text-sm text-slate-700 dark:text-slate-300">
                <span className="font-semibold text-blue-700 dark:text-blue-400 block mb-1">Advice/Strategy:</span>
                {q.advice}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (activeTab === 'questionsToAsk') {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Questions to Ask the Interviewer</h3>
          <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{data.questionsToAsk.description}</p>
        </div>
        <div className="space-y-4">
          {data.questionsToAsk.questions.map((q, i) => (
            <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm">
              <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3">{q.question}</h4>
              <div className="bg-indigo-50/50 dark:bg-indigo-900/10 rounded-lg p-4 text-sm text-slate-700 dark:text-slate-300">
                <span className="font-semibold text-indigo-700 dark:text-indigo-400 block mb-1">Context:</span>
                {q.context}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (activeTab === 'skillsGap') {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Skills Gap & Weaknesses</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.skillsGap.weaknesses.map((w, i) => (
            <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm flex flex-col items-start gap-3">
              <Badge variant="warning">{w.topic}</Badge>
              <div className="text-sm text-slate-700 dark:text-slate-300">
                <span className="font-semibold text-amber-700 dark:text-amber-400 block mb-1">Strategy:</span>
                {w.strategy}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (activeTab === 'learningPath') {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Learning Path</h3>
        </div>
        <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-300 dark:before:via-slate-700 before:to-transparent">
          {data.learningPath.steps.map((s, i) => (
            <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
               <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white dark:border-slate-900 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                 <span className="font-semibold text-sm">{i + 1}</span>
               </div>
               
               <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                 <div className="flex flex-col gap-1.5">
                   <h4 className="font-medium text-slate-900 dark:text-slate-200 text-sm leading-snug">{s.action}</h4>
                   <div className="text-xs text-slate-500 dark:text-slate-400 font-medium bg-slate-50 dark:bg-slate-800/50 p-2 rounded-md">
                     <span className="text-emerald-600 dark:text-emerald-400 mr-2">Resource:</span>
                     {s.resource}
                   </div>
                 </div>
               </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return null;
};
